import { C, fonts } from "../theme.js";
import { SectionLabel } from "./primitives.jsx";
import { CASES } from "../content.js";

const CaseArt = ({ kind, accent }) => {
  if (kind === "doc")
    return (
      <svg viewBox="0 0 200 120" width="100%" style={{ display: "block" }}>
        <rect x="20" y="14" width="80" height="92" fill={C.bg} stroke={C.borderStrong} />
        <line x1="32" y1="34" x2="88" y2="34" stroke={C.faint} />
        <line x1="32" y1="48" x2="88" y2="48" stroke={C.faint} />
        <line x1="32" y1="62" x2="72" y2="62" stroke={C.faint} />
        <rect
          x="32"
          y="76"
          width="34"
          height="14"
          fill={accent}
          fillOpacity="0.25"
          stroke={accent}
          strokeWidth="0.6"
        />
        <path d="M104 60 L130 60" stroke={accent} strokeDasharray="3 3" />
        <rect x="132" y="32" width="56" height="56" rx="4" fill="none" stroke={accent} />
        <text x="160" y="65" fontFamily="'JetBrains Mono'" fontSize="10" fill={accent} textAnchor="middle">
          JSON
        </text>
      </svg>
    );
  if (kind === "stream")
    return (
      <svg viewBox="0 0 200 120" width="100%">
        {[0, 1, 2, 3].map((i) => (
          <path
            key={i}
            d={`M 0 ${30 + i * 20} Q 50 ${20 + i * 20}, 100 ${30 + i * 20} T 200 ${30 + i * 20}`}
            stroke={i === 1 ? accent : C.faint}
            strokeWidth={i === 1 ? "1.6" : "0.8"}
            fill="none"
            opacity={i === 1 ? 1 : 0.5}
          />
        ))}
        {[40, 90, 140].map((x, i) => (
          <circle key={i} cx={x} cy={50} r="3" fill={accent}>
            <animate
              attributeName="cx"
              from={x - 30}
              to={x + 30}
              dur="3s"
              repeatCount="indefinite"
              begin={`${i * 0.4}s`}
            />
          </circle>
        ))}
      </svg>
    );
  return (
    <svg viewBox="0 0 200 120" width="100%">
      <circle cx="100" cy="60" r="22" fill="none" stroke={accent} strokeWidth="1.4" />
      <text x="100" y="64" fontFamily="'JetBrains Mono'" fontSize="11" fill={accent} textAnchor="middle">
        CLAUDE
      </text>
      {[[40, 30], [40, 90], [160, 30], [160, 90]].map(([x, y], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r="14" fill="none" stroke={C.faint} />
          <text x={x} y={y + 4} fontFamily="'JetBrains Mono'" fontSize="9" fill={C.body} textAnchor="middle">
            A{i + 1}
          </text>
          <line
            x1={x < 100 ? x + 14 : x - 14}
            y1={y}
            x2={x < 100 ? 78 : 122}
            y2={60}
            stroke={accent}
            strokeOpacity="0.5"
            strokeDasharray="2 2"
          />
        </g>
      ))}
    </svg>
  );
};

export const Cases = ({ accent }) => (
  <section id="work" style={{ background: C.about, padding: "140px 40px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 24,
          marginBottom: 56,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <SectionLabel accent={accent}>// SELECTED WORK</SectionLabel>
          <h2
            style={{
              margin: 0,
              fontFamily: fonts.serif,
              fontSize: "clamp(36px, 4.4vw, 56px)",
              fontWeight: 500,
              color: C.fg,
              letterSpacing: "-0.01em",
              lineHeight: 1.05,
            }}
          >
            Shipped, <em style={{ color: accent, fontStyle: "italic", fontWeight: 500 }}>in production.</em>
          </h2>
        </div>
        <p
          style={{
            margin: 0,
            fontFamily: fonts.mono,
            fontSize: 13,
            color: C.body,
            maxWidth: 360,
            lineHeight: 1.65,
          }}
        >
          Three recent engagements. Names anonymized — happy to share details on a call.
        </p>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 20,
        }}
      >
        {CASES.map((cs, i) => (
          <article
            key={i}
            style={{
              background: C.card,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                aspectRatio: "200 / 120",
                background: C.bg,
                borderBottom: `1px solid ${C.border}`,
                padding: 18,
              }}
            >
              <CaseArt kind={cs.art} accent={accent} />
            </div>
            <div
              style={{
                padding: "26px 26px 22px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                flex: 1,
              }}
            >
              <div style={{ fontFamily: fonts.mono, fontSize: 10, letterSpacing: "0.22em", color: accent }}>
                {cs.tag}
              </div>
              <h3
                style={{
                  margin: 0,
                  fontFamily: fonts.serif,
                  fontSize: 24,
                  fontWeight: 500,
                  color: C.fg,
                  letterSpacing: "-0.005em",
                  lineHeight: 1.2,
                }}
              >
                {cs.title}
              </h3>
              <div
                style={{
                  fontFamily: fonts.serif,
                  fontSize: 18,
                  fontStyle: "italic",
                  color: accent,
                }}
              >
                {cs.metric}
              </div>
              <p style={{ margin: 0, fontFamily: fonts.sans, fontSize: 13.5, lineHeight: 1.65, color: C.body }}>
                {cs.body}
              </p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                {cs.stack.map((s) => (
                  <span
                    key={s}
                    style={{
                      fontFamily: fonts.mono,
                      fontSize: 10,
                      letterSpacing: "0.08em",
                      padding: "4px 8px",
                      border: `1px solid ${C.border}`,
                      color: C.body,
                      borderRadius: 4,
                    }}
                  >
                    {s}
                  </span>
                ))}
              </div>
              <div style={{ marginTop: "auto", paddingTop: 16, borderTop: `1px dashed ${C.border}` }}>
                <a
                  href="#contact"
                  style={{
                    fontFamily: fonts.mono,
                    fontSize: 11,
                    letterSpacing: "0.18em",
                    color: accent,
                    textDecoration: "none",
                  }}
                >
                  READ CASE STUDY ↗
                </a>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);
